import { useState, useEffect, useCallback } from "react";
import { ArrowClockwise, FloppyDisk, Info, Warning, Key } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Badge colour per model tier
function ModelBadge({ model }) {
  const colour =
    model?.includes("gpt-4o") && !model?.includes("mini")
      ? "bg-orange-50 text-orange-700 border-orange-200"
      : model?.includes("mini") || model?.includes("3.5")
      ? "bg-blue-50 text-blue-700 border-blue-200"
      : "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span className={`mono-label px-2 py-0.5 border ${colour}`}>{model || "—"}</span>
  );
}

export default function LLMKeyAdmin() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSections, setSavingSections] = useState(false);
  const [sectionModels, setSectionModels] = useState({});
  const [dirty, setDirty] = useState(false);

  // Base model / API key panel — separate save flow from section overrides.
  const [savingBase, setSavingBase] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [baseModelInput, setBaseModelInput] = useState("");

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/llm-config");
      setConfig(data);
      setBaseModelInput(data.model_id || "");
      // Seed local section-model state from API response
      const sm = {};
      (data.usages || []).forEach((u) => { sm[u.section_key] = u.model; });
      setSectionModels(sm);
      setDirty(false);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      toast.error(`Failed to load LLM config${status ? ` (${status})` : ""}${detail ? `: ${detail}` : ""}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSectionModelChange = (sectionKey, value) => {
    setSectionModels((prev) => ({ ...prev, [sectionKey]: value }));
    setDirty(true);
  };

  const handleSaveSections = async () => {
    setSavingSections(true);
    try {
      await api.patch("/admin/llm-config/sections", { section_models: sectionModels });
      toast.success("Section models saved");
      setDirty(false);
      await fetchConfig();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save section models");
    } finally {
      setSavingSections(false);
    }
  };

  const handleSaveBase = async (e) => {
    e.preventDefault();
    if (!apiKeyInput.trim()) {
      toast.error("Enter the API key to rotate/set it");
      return;
    }
    if (!baseModelInput.trim()) {
      toast.error("Base model is required");
      return;
    }
    setSavingBase(true);
    try {
      await api.put("/admin/llm-config", {
        provider: "openai",
        api_key: apiKeyInput.trim(),
        model_id: baseModelInput.trim(),
      });
      toast.success("Base model + API key updated");
      setApiKeyInput("");
      await fetchConfig();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update base config");
    } finally {
      setSavingBase(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="mono-label mb-1">Admin · Configuration</div>
          <h1 className="font-display text-3xl text-slate-900 leading-none">
            LLM Key<span className="text-orange-500">.</span>Admin
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Manage the API key and per-section model used across all AI-powered features.
          </p>
        </div>
        <button onClick={fetchConfig} className="btn-sharp btn-ghost flex items-center gap-2 text-sm">
          <ArrowClockwise size={15} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="mono-label pulse-soft text-slate-400 py-16 text-center">Loading config…</div>
      ) : (
        <>
          {/* ── Base model / API key ── */}
          <div className="card-sharp border border-slate-200 bg-white">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3 flex-wrap">
              <Key size={16} className="text-slate-400" />
              <span className="font-display text-lg text-slate-900">Base Model &amp; API Key</span>
              <span className="ml-auto text-xs text-slate-400">
                Used as the fallback for any section without its own override
              </span>
            </div>
            <div className="px-6 py-4 grid gap-4 sm:grid-cols-2">
              <div className="text-sm">
                <div className="mono-label text-slate-400 mb-1">Current base model</div>
                <ModelBadge model={config?.model_id} />
              </div>
              <div className="text-sm">
                <div className="mono-label text-slate-400 mb-1">API key</div>
                <span className="font-mono text-xs text-slate-600">
                  {config?.api_key_configured ? (config?.api_key_masked || "***configured***") : "Not configured"}
                </span>
              </div>
              <div className="text-sm">
                <div className="mono-label text-slate-400 mb-1">Config source</div>
                <span className="text-xs text-slate-600 capitalize">{config?.source || "—"}</span>
              </div>
              <div className="text-sm">
                <div className="mono-label text-slate-400 mb-1">Last updated</div>
                <span className="text-xs text-slate-600">
                  {config?.updated_at ? new Date(config.updated_at).toLocaleString() : "—"}
                  {config?.updated_by ? ` · ${config.updated_by}` : ""}
                </span>
              </div>
            </div>
            <form onSubmit={handleSaveBase} className="px-6 py-4 border-t border-slate-100 grid gap-3 sm:grid-cols-[1fr_1fr_auto] items-end">
              <label className="text-sm">
                <div className="mono-label text-slate-400 mb-1">New API key</div>
                <input
                  type="password"
                  autoComplete="off"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder="sk-…"
                  className="w-full border border-slate-200 bg-white text-xs font-mono px-2 py-1.5 focus:outline-none focus:border-orange-400 transition-colors"
                  style={{ borderRadius: 2 }}
                />
              </label>
              <label className="text-sm">
                <div className="mono-label text-slate-400 mb-1">Base model</div>
                <select
                  value={baseModelInput}
                  onChange={(e) => setBaseModelInput(e.target.value)}
                  className="w-full border border-slate-200 bg-white text-xs font-mono px-2 py-1.5 focus:outline-none focus:border-orange-400 transition-colors"
                  style={{ borderRadius: 2 }}
                >
                  {(config?.usages?.[0]?.model_options || [config?.model_id].filter(Boolean)).map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </label>
              <button
                type="submit"
                disabled={savingBase}
                className="btn-sharp btn-primary flex items-center gap-1.5 text-xs px-3 py-1.5 h-fit"
              >
                <FloppyDisk size={13} weight="bold" />
                {savingBase ? "Saving…" : "Update base config"}
              </button>
            </form>
            <div className="px-6 pb-4 text-xs text-slate-400 flex items-start gap-1.5">
              <Info size={13} className="shrink-0 mt-0.5" />
              Rotating the key or base model here does not touch per-section overrides below.
            </div>
          </div>

          {/* ── Usage Map ── */}
          <div className="card-sharp border border-slate-200 bg-white">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3 flex-wrap">
              <Info size={16} className="text-slate-400" />
              <span className="font-display text-lg text-slate-900">LLM Usage Map</span>
              <span className="mono-label bg-slate-100 text-slate-500 px-2 py-0.5">
                {config?.usages?.length || 0} sections
              </span>
              <span className="ml-auto text-xs text-slate-400">
                Each section can use a different model
              </span>
              {dirty && (
                <button
                  onClick={handleSaveSections}
                  disabled={savingSections}
                  className="btn-sharp btn-primary flex items-center gap-1.5 text-xs px-3 py-1.5"
                >
                  <FloppyDisk size={13} weight="bold" />
                  {savingSections ? "Saving…" : "Save section models"}
                </button>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left px-6 py-3 mono-label text-slate-500 font-normal w-48">Section</th>
                    <th className="text-left px-4 py-3 mono-label text-slate-500 font-normal">File</th>
                    <th className="text-left px-4 py-3 mono-label text-slate-500 font-normal hidden lg:table-cell">Endpoint</th>
                    <th className="text-left px-4 py-3 mono-label text-slate-500 font-normal w-56">Model</th>
                    <th className="text-left px-4 py-3 mono-label text-slate-500 font-normal hidden xl:table-cell">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {(config?.usages || []).map((u, i) => {
                    const currentModel = sectionModels[u.section_key] || u.model;
                    const changed = currentModel !== u.model;
                    return (
                      <tr key={i} className={`border-b border-slate-50 transition-colors ${changed ? "bg-orange-50/40" : "hover:bg-slate-50"}`}>
                        <td className="px-6 py-3 font-medium text-slate-800 whitespace-nowrap">
                          {u.section}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-sm">
                            {u.file}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500 font-mono whitespace-nowrap hidden lg:table-cell">
                          {u.endpoint}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <select
                              value={currentModel}
                              onChange={(e) => handleSectionModelChange(u.section_key, e.target.value)}
                              className="border border-slate-200 bg-white text-xs font-mono px-2 py-1.5 focus:outline-none focus:border-orange-400 transition-colors"
                              style={{ borderRadius: 2 }}
                            >
                              {(u.model_options || [currentModel]).map((m) => (
                                <option key={m} value={m}>{m}</option>
                              ))}
                            </select>
                            {changed && (
                              <Warning size={14} className="text-orange-500 shrink-0" weight="fill" />
                            )}
                          </div>
                          {/* Vision-only note */}
                          {u.section_key === "image_ocr" && (
                            <p className="text-xs text-slate-400 mt-0.5">Vision-capable models only</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500 hidden xl:table-cell">
                          {u.description}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {dirty && (
              <div className="px-6 py-3 border-t border-orange-100 bg-orange-50/30 flex items-center justify-between">
                <span className="text-xs text-orange-700 flex items-center gap-1.5">
                  <Warning size={13} weight="fill" /> Unsaved changes — click Save to apply
                </span>
                <button
                  onClick={handleSaveSections}
                  disabled={savingSections}
                  className="btn-sharp btn-primary flex items-center gap-1.5 text-xs px-3 py-1.5"
                >
                  <FloppyDisk size={13} weight="bold" />
                  {savingSections ? "Saving…" : "Save section models"}
                </button>
              </div>
            )}
          </div>

        </>
      )}
    </div>
  );
}

